import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import {
  useAssignCollectorGroupsMutation,
  useCancelInviteMutation,
  useInviteOrgUserMutation,
  useOrgUsersQuery,
  useResendInviteMutation,
  useUpdateOrgUserMutation,
  type OrgUser,
} from "../../api/queries/orgUsersQueries";
import { useCustomerGroupsQuery } from "../../api/queries/customerGroupsQueries";
import type { OrgRole, PageScope } from "../../utils/types";
import { ALL_PAGE_SCOPES } from "../../utils/types";
import { formatAppDate } from "../../utils/date";

const CONFIGURABLE_SCOPES: PageScope[] = ALL_PAGE_SCOPES.filter(
  (s) => s !== "ADMIN",
);

export function OrgUsersPage() {
  const { scopes: managerScopes } = useAuth();
  const grantableScopes = useMemo(
    () => CONFIGURABLE_SCOPES.filter((s) => managerScopes?.includes(s)),
    [managerScopes],
  );
  const { data, isLoading } = useOrgUsersQuery();
  const { data: groups = [] } = useCustomerGroupsQuery();
  const inviteMutation = useInviteOrgUserMutation();
  const updateMutation = useUpdateOrgUserMutation();
  const assignGroupsMutation = useAssignCollectorGroupsMutation();
  const resendMutation = useResendInviteMutation();
  const cancelMutation = useCancelInviteMutation();

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrgRole>("COLLECTOR");
  const [invitePhone, setInvitePhone] = useState("");
  const defaultInviteScopes = useMemo(
    () =>
      grantableScopes.includes("RECEIVABLES")
        ? (["RECEIVABLES"] as PageScope[])
        : grantableScopes.slice(0, 1),
    [grantableScopes],
  );
  const [inviteScopes, setInviteScopes] = useState<PageScope[]>([]);

  const [editUser, setEditUser] = useState<OrgUser | null>(null);
  const [editPhone, setEditPhone] = useState("");
  const [editRole, setEditRole] = useState<OrgRole>("COLLECTOR");
  const [editScopes, setEditScopes] = useState<PageScope[]>([]);
  const [editGroupIds, setEditGroupIds] = useState<number[]>([]);

  const members = data?.members ?? [];
  const invites = data?.pending_invitations ?? [];

  const openEdit = (user: OrgUser) => {
    setEditUser(user);
    setEditPhone(user.phone_number ?? "");
    setEditRole(user.org_role ?? "COLLECTOR");
    setEditScopes(user.page_scopes.filter((s) => grantableScopes.includes(s)));
    setEditGroupIds(user.assigned_groups.map((g) => g.id));
  };

  const toggleScope = (scope: PageScope) => {
    setInviteScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const toggleEditScope = (scope: PageScope) => {
    setEditScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const handleInvite = async () => {
    try {
      await inviteMutation.mutateAsync({
        email: inviteEmail,
        org_role: inviteRole,
        phone_number: invitePhone || undefined,
        page_scopes: inviteScopes,
      });
      toast.success("Invitation sent");
      setShowInvite(false);
      setInviteEmail("");
      setInvitePhone("");
    } catch {
      toast.error("Failed to send invitation");
    }
  };

  const handleSaveUser = async () => {
    if (!editUser) return;
    try {
      await updateMutation.mutateAsync({
        userId: editUser.id,
        org_role: editRole,
        phone_number: editPhone || null,
        page_scopes: editScopes,
      });
      if (editRole === "COLLECTOR") {
        await assignGroupsMutation.mutateAsync({
          userId: editUser.id,
          group_ids: editGroupIds,
        });
      }
      toast.success("User updated");
      setEditUser(null);
    } catch {
      toast.error("Failed to update user");
    }
  };

  const handleDeactivate = async (user: OrgUser) => {
    const deactivating = user.membership_status === "active";
    if (
      deactivating &&
      !window.confirm(
        `Deactivate ${user.username || user.email}? They will lose access until reactivated.`,
      )
    ) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        userId: user.id,
        membership_status: deactivating ? "deactivated" : "active",
      });
      toast.success("User status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading users…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            Organization users
          </h2>
          <p className="text-sm text-gray-500">
            Invite managers and collectors, configure permissions and customer
            groups.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setInviteScopes(defaultInviteScopes);
            setShowInvite(true);
          }}
          className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
        >
          Add user
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Groups</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((user) => (
              <tr key={user.id} className="border-t border-gray-100">
                <td className="px-4 py-3">{user.username}</td>
                <td className="px-4 py-3">{user.email}</td>
                <td className="px-4 py-3">{user.org_role ?? "—"}</td>
                <td className="px-4 py-3">{user.phone_number ?? "—"}</td>
                <td className="px-4 py-3 capitalize">
                  {user.membership_status}
                </td>
                <td className="px-4 py-3">
                  {user.assigned_groups.map((g) => g.name).join(", ") || "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="text-teal-700 hover:underline"
                      onClick={() => openEdit(user)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="text-gray-600 hover:underline"
                      onClick={() => handleDeactivate(user)}
                    >
                      {user.membership_status === "active"
                        ? "Deactivate"
                        : "Activate"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invites.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-gray-800">
            Pending invitations
          </h3>
          <div className="rounded-xl border border-gray-200 bg-white divide-y">
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <div>
                  <p className="font-medium text-gray-800">{invite.email}</p>
                  <p className="text-gray-500">
                    {invite.org_role} · expires {formatAppDate(invite.expires_at)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-teal-700 hover:underline"
                    onClick={() =>
                      resendMutation.mutate(invite.id, {
                        onSuccess: () => toast.success("Invitation resent"),
                      })
                    }
                  >
                    Resend
                  </button>
                  <button
                    type="button"
                    className="text-red-600 hover:underline"
                    onClick={() =>
                      cancelMutation.mutate(invite.id, {
                        onSuccess: () => toast.success("Invitation cancelled"),
                      })
                    }
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800">Invite user</h3>
            <div className="mt-4 space-y-3">
              <input
                type="email"
                placeholder="Email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as OrgRole)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="MANAGER">Manager</option>
                <option value="COLLECTOR">Collector</option>
                <option value="VIEWER">Viewer</option>
              </select>
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={invitePhone}
                onChange={(e) => setInvitePhone(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="flex flex-wrap gap-2">
                {grantableScopes.map((scope) => (
                  <button
                    key={scope}
                    type="button"
                    onClick={() => toggleScope(scope)}
                    className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ${
                      inviteScopes.includes(scope)
                        ? "bg-teal-600 text-white ring-teal-600"
                        : "bg-white text-gray-700 ring-gray-300"
                    }`}
                  >
                    {scope}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInvite}
                disabled={
                  !inviteEmail ||
                  inviteScopes.length === 0 ||
                  inviteMutation.isPending
                }
                className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Send invite
              </button>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800">Edit user</h3>
            <p className="text-sm text-gray-500">{editUser.email}</p>
            <div className="mt-4 space-y-3">
              <select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value as OrgRole)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="MANAGER">Manager</option>
                <option value="COLLECTOR">Collector</option>
                <option value="VIEWER">Viewer</option>
              </select>
              <input
                type="tel"
                placeholder="Phone"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <>
                <p className="text-xs font-semibold uppercase text-gray-500">
                  Page permissions
                </p>
                <div className="flex flex-wrap gap-2">
                  {grantableScopes.map((scope) => (
                    <button
                      key={scope}
                      type="button"
                      onClick={() => toggleEditScope(scope)}
                      className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ${
                        editScopes.includes(scope)
                          ? "bg-teal-600 text-white ring-teal-600"
                          : "bg-white text-gray-700 ring-gray-300"
                      }`}
                    >
                      {scope}
                    </button>
                  ))}
                </div>
                {editRole === "COLLECTOR" && (
                  <>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Customer groups
                    </p>
                    <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
                      {groups.map((group) => (
                        <label
                          key={group.id}
                          className="flex items-center gap-2 text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={editGroupIds.includes(group.id)}
                            onChange={(e) => {
                              setEditGroupIds((prev) =>
                                e.target.checked
                                  ? [...prev, group.id]
                                  : prev.filter((id) => id !== group.id),
                              );
                            }}
                          />
                          {group.name}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditUser(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveUser}
                disabled={updateMutation.isPending}
                className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
